/**
 * EventDetailsCard.js — Detailed Event Card (cardType "eventDetails").
 *
 * Renders the single detailed_event_information feature card: the same event
 * fields as EventCard but in a fuller layout — full (un-clamped) description,
 * a divider, date/time/location, price, and a reservation CTA button. The BE
 * returns exactly one card in feature_cards for this type (see
 * handle_unique_id_lookup -> DETAILED_EVENT_INFORMATION).
 *
 * BE FIELDS (event/reserved_event shape; produced by handle_unique_id_lookup):
 *   title, description, date, time, location, price, price_unit,
 *   cta_text, cta_link, cover_image_link
 *   (price may arrive pre-split on "/" by the BE for the detail view.)
 *
 * REFERENCE (old code):
 *   thomasfogarty/script.js createEventDetails() (2129-2158)
 */

export default class EventDetailsCard {
  /**
   * @param {Object} ev - detailed event feature card
   * @returns {HTMLElement}
   */
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-details-card";

    if (ev.cover_image_link) {
      const head = document.createElement("div");
      head.className = "ws-card-head";
      const img = document.createElement("img");
      img.className = "ws-card-img";
      img.src = ev.cover_image_link;
      img.alt = ev.title || "Event";
      img.loading = "lazy";
      head.appendChild(img);
      card.appendChild(head);
    }

    const body = document.createElement("div");
    body.className = "ws-card-body";

    if (ev.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = ev.title;
      title.textContent = ev.title;
      body.appendChild(title);
    }

    // description may be a string or (legacy) an array of paragraphs.
    const paragraphs = Array.isArray(ev.description)
      ? ev.description
      : ev.description
        ? [ev.description]
        : [];
    if (paragraphs.length) {
      const hr = document.createElement("div");
      hr.className = "ws-card-divider";
      body.appendChild(hr);
      paragraphs.forEach((text) => {
        const p = document.createElement("p");
        p.className = "ws-card-desc-full";
        p.textContent = text;
        body.appendChild(p);
      });
    }

    const metaLines = [ev.date, ev.time].filter(Boolean);
    metaLines.forEach((text) => {
      const p = document.createElement("p");
      p.className = "ws-card-meta ws-card-meta-secondary";
      p.textContent = text;
      body.appendChild(p);
    });
    const metaOthersLines = [ev.location].filter(Boolean);
    metaOthersLines.forEach((text) => {
      const p = document.createElement("p");
      p.className = "ws-card-meta";
      p.textContent = text;
      body.appendChild(p);
    });

    const priceText = [ev.price, ev.price_unit].filter(Boolean).join(" ");
    if (priceText) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = priceText;
      body.appendChild(price);
    }

    if (ev.cta_link) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta";
      cta.href = ev.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = ev.cta_text || "Reserve";
      body.appendChild(cta);
    }

    card.appendChild(body);
    return card;
  }
}
